import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatIDNumber, formatIDPercent } from "../../utils";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { capitalizeFirstLetter } from "src/utils/methods";
import { Button } from "src/app/components/Button/Button";

import { ReactComponent as SortIcon } from "src/images/up-down.svg";
import { ReactComponent as TickIcon } from "src/images/tick.svg";
import { Img } from "src/app/components/Img/Img";
import classNames from "classnames";
import { ReactComponent as InfoIcon } from "src/images/info-circle-blue-empty.svg";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

export interface InsightsTableRow {
  id: string;
  label: string;
  posts: number | string;
  er: number;
  url?: string;
  imageUrl: string | null;
  fallbackImageUrl: string;
}

interface Props {
  title: string;
  firstColumnLabel: string;
  data: { er: InsightsTableRow[]; posts: InsightsTableRow[] };
  emptyState: {
    title: string;
    description: string;
  };
}

type SortBy = "er" | "posts";

export const InsightsTable: React.FC<Props> = ({
  title,
  firstColumnLabel,
  data,
  emptyState,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("posts");

  const { printing } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const showMore = expanded || printing;

  const rows = data[sortBy];

  const displayImage = rows.some((row) => row.imageUrl !== null);

  const visibleRows = showMore ? rows : rows.slice(0, 5);

  const { pathname } = useLocation();

  const getRowUrl = (row: InsightsTableRow) => {
    switch (firstColumnLabel) {
      case "Creator":
        return {
          pathname,
          search: `?type=influencer&value=${row.id}`,
        };

      default:
        return {
          pathname,
          search: `?type=${firstColumnLabel.toLowerCase()}&value=${row.label.replace(/^[#@]+/, "")}`,
        };
    }
  };

  const renderValue = (value: number | string) => {
    if (typeof value === "string") return value;

    if (sortBy === "er") {
      return formatIDPercent(value);
    }

    return formatIDNumber(value);
  };

  const sortByOptions: { label: string; value: SortBy }[] = [
    {
      label: "Posts",
      value: "posts",
    },
    {
      label: "ER",
      value: "er",
    },
  ];

  return (
    <section className="id-top-insights__section">
      <div className="id-top-insights__header">
        <span className="id-top-insights__title">{title}</span>

        {!printing && (
          <Dropdown>
            {(open, setOpen) => (
              <>
                <Button
                  variant="white"
                  size="small"
                  onClick={() => setOpen(!open)}
                >
                  <SortIcon />
                  Sort by: {capitalizeFirstLetter(sortBy)}
                </Button>

                <DropdownMenu isOpen={open}>
                  {sortByOptions.map((item) => (
                    <DropdownMenuItem
                      onClick={() => {
                        setSortBy(item.value);
                        setOpen(false);
                      }}
                    >
                      {item.label}
                      {sortBy === item.value && <TickIcon />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        )}
      </div>

      <div className="id-top-insights__table">
        <div className="id-top-insights__table-head">
          <span>{firstColumnLabel}</span>
          <span>{sortBy}</span>
        </div>

        {visibleRows.map((row, index) => {
          const rowClassName = classNames("id-top-insights__row", {
            "id-top-insights__row--with-image": displayImage,
          });
          const content = (
            <>
              <span className="id-top-insights__index">{index + 1}</span>
              {displayImage && (
                <Img
                  src={row?.imageUrl || undefined}
                  fallbackImageSrc={row?.fallbackImageUrl}
                  alt="image"
                  className="id-top-insights__image"
                />
              )}
              <span className="id-top-insights__primary">{row.label}</span>
              <span className="id-top-insights__value">
                {renderValue(row[sortBy])}
              </span>
            </>
          );

          if (printing) {
            return (
              <div key={row.id} className={rowClassName}>
                {content}
              </div>
            );
          }

          return (
            <Link
              key={row.id}
              to={getRowUrl(row)}
              rel="noreferrer"
              className={rowClassName}
            >
              {content}
            </Link>
          );
        })}

        {!rows.length && (
          <div className="id-top-insights__empty-state">
            <InfoIcon className="id-top-insights__empty-state-icon" />
            <p className="id-top-insights__empty-state-title">
              {emptyState.title}
            </p>
            <p className="id-top-insights__empty-state-description">
              {emptyState.description}
            </p>
          </div>
        )}

        {rows.length > 5 && !printing && (
          <button
            type="button"
            className="id-top-insights__show-more"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {showMore ? "Show less" : "Show more"}
          </button>
        )}
      </div>
    </section>
  );
};
